import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePortalAccess } from '../hooks/usePortalAccess';
import { useCompanyId } from '../hooks/useCompanyId';
import { getAccountById } from '../services/firestoreService';
import { saveKickoffTourProgress, saveKickoffCallBooking } from '../services/kickoffProgressService';
import { getPortalEmoji } from '../utils/portalEmojis';
import PortalBackground from './PortalBackground';
import KickoffCallScheduler from './portal/KickoffCallScheduler';
import BBALoader from './BBALoader';

const pad = (n) => String(n).padStart(2, '0');

// The Getting Started stepper: kickoff and profile are real, interactive steps
// the client completes (phase 'kickoff'); the rest are locked look-don't-touch
// previews of each portal area (phase 'tour', shown in the Tour section). Each
// step navigates to the real page so the sidebar highlights it.
const TOUR_STOPS = [
  { id: 'kickoff', phase: 'kickoff', title: 'Your Priorities', path: '/portal/kickoff', emoji: '🚀', interactive: true, description: 'Set your top priorities across every department — tap a card to raise its priority, then drag your Top Priorities into order. It saves as you go.' },
  { id: 'profile', phase: 'kickoff', title: 'Profile Setup', path: '/portal/profile', emoji: '👤', interactive: true, description: 'Fill out your personal and company info one by one — photo, logo, theme, billing, notifications, and integrations.' },
  { id: 'dashboard', title: 'Dashboard', path: '/portal', description: 'Your home base — what\'s new, your tasks, goals, upcoming events, and quick ways to reach your team.' },
  { id: 'roadmap', title: 'Roadmap', path: '/portal/roadmap', description: 'Your program roadmap — every step of your journey with us, what\'s in progress, and what\'s coming next.' },
  { id: 'courses', title: 'Courses', path: '/portal/courses', description: 'On-demand training courses for you and your team, available any time.' },
  { id: 'tasks', title: 'Tasks', path: '/portal/tasks', description: 'Tasks from your BBA team — what we need from you and what we\'re working on for you.' },
  { id: 'community', title: 'Community', path: '/portal/community', description: 'Connect with other builders in the BBA community — share wins, ask questions, and learn.' },
  { id: 'sales', title: 'Sales', path: '/portal/sales', description: 'Sales materials, proposals, partner programs, and analytics to help you close more work.' },
  { id: 'operations', title: 'Operations', path: '/portal/operations', description: 'SOPs, KPIs, and the systems that keep your business running smoothly.' },
  { id: 'technology', title: 'Technology', path: '/portal/technology', description: 'Your tech stack, JobTread imports, automations, and AI tools.' },
  { id: 'finance', title: 'Finance', path: '/portal/finance', description: 'Financial analytics, budgeting, and tools to keep your numbers healthy.' },
  { id: 'marketing', title: 'Marketing', path: '/portal/marketing', description: 'Lead tracking, marketing analytics, and tools to grow your pipeline.' },
  { id: 'hr', title: 'HR', path: '/portal/hr', description: 'Hiring, employee materials, and your company direction — everything people-related.' },
  { id: 'collection', title: 'Collection', path: '/portal/collection', interactive: true, hint: 'Your documents live here — this page is fully available to you now.', description: 'Your library of documents and deliverables, all in one place.' },
];

// Both phases keep their real index into TOUR_STOPS — the stepper, the visited
// set, and the saved progress ids are all index/id based off the one array.
const withIndex = TOUR_STOPS.map((stop, index) => ({ ...stop, index }));
const KICKOFF_STEPS = withIndex.filter((stop) => stop.phase === 'kickoff');
const TOUR_PAGE_STEPS = withIndex.filter((stop) => stop.phase !== 'kickoff');

const stopEmoji = (stop) => stop.emoji || getPortalEmoji(stop.id);

const normalizePath = (p) => (p || '').replace(/\/+$/, '') || '/';

// Ticks once a second toward the start date; returns null until a target exists.
const useCountdown = (target) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = Math.max(0, target.getTime() - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor(diff / 3600000) % 24,
    minutes: Math.floor(diff / 60000) % 60,
    seconds: Math.floor(diff / 1000) % 60,
    done: diff <= 0,
  };
};

/**
 * Content-area gate for clients whose program start date hasn't arrived yet.
 * The portal shell (sidebar, nav) stays visible; page content is replaced with
 * a countdown plus a Getting Started stepper that walks them through their
 * kickoff priorities, profile setup, and then a locked page-by-page portal tour.
 * Kickoff and profile steps are fully interactive; tour pages are previews.
 */
const PortalPendingStartGate = ({ children }) => {
  const { isPendingStart, programStartDate, loading } = usePortalAccess();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId } = useCompanyId();
  const [tourIndex, setTourIndex] = useState(null);
  const [visitedTourStops, setVisitedTourStops] = useState(() => new Set());
  const [callBooking, setCallBooking] = useState(null); // kickoff call, step 1
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const tourNavRef = useRef(null); // path of a tour navigation still in flight
  const savedStopsRef = useRef(null); // signature of the stop list already persisted
  const savingBookingRef = useRef(false); // Calendly can fire event_scheduled twice

  // Rehydrate progress from the account so refreshing (or coming back tomorrow)
  // keeps the Continue button and the green step cards — and so the success
  // manager sees the same picture on the CRM kickoff page.
  useEffect(() => {
    if (!companyId || !isPendingStart) return undefined;
    let cancelled = false;
    getAccountById(companyId)
      .then((account) => {
        if (cancelled) return;
        if (account?.kickoffCallBooking) setCallBooking(account.kickoffCallBooking);
        const visited = account?.kickoffTourProgress?.visitedStops;
        if (!Array.isArray(visited) || visited.length === 0) return;
        const indices = visited
          .map((id) => TOUR_STOPS.findIndex((s) => s.id === id))
          .filter((i) => i >= 0)
          .sort((a, b) => a - b);
        if (!indices.length) return;
        // Mark what we just read as already-saved so the persist effect below
        // treats this as a no-op. If they clicked ahead while this was loading,
        // the merged set won't match and it saves for real.
        savedStopsRef.current = indices.map((i) => TOUR_STOPS[i].id).join('|');
        setVisitedTourStops((prev) => new Set([...prev, ...indices]));
      })
      .catch((err) => console.error('Error loading kickoff tour progress:', err));
    return () => { cancelled = true; };
  }, [companyId, isPendingStart]);

  const openTourAt = useCallback((index) => {
    const stop = TOUR_STOPS[index];
    if (!stop) return;
    setTourIndex(index);
    setVisitedTourStops((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
    tourNavRef.current = normalizePath(stop.path);
    navigate(stop.path);
  }, [navigate]);

  // Persist visited stops whenever they grow. savedStopsRef holds what's already
  // on the account, so rehydrating on mount doesn't write back what we just read.
  useEffect(() => {
    if (!companyId || !isPendingStart || visitedTourStops.size === 0) return;
    const ids = [...visitedTourStops].sort((a, b) => a - b).map((i) => TOUR_STOPS[i]?.id).filter(Boolean);
    const signature = ids.join('|');
    if (savedStopsRef.current === signature) return;
    savedStopsRef.current = signature;
    saveKickoffTourProgress(companyId, ids, TOUR_STOPS.length).catch((err) =>
      console.error('Error saving kickoff tour progress:', err)
    );
  }, [visitedTourStops, companyId, isPendingStart]);

  const closeTour = useCallback(() => setTourIndex(null), []);

  // Step 1 completion. Fires from the Calendly embed's event_scheduled message,
  // or from the "already booked" escape hatch for a client who scheduled from
  // their email instead. First one through wins — reopening to reschedule must
  // not overwrite the original booking record.
  const handleCallScheduled = useCallback((details = {}) => {
    if (savingBookingRef.current) return;
    savingBookingRef.current = true;
    const optimistic = { ...details, scheduledAt: new Date().toISOString() };
    setCallBooking((prev) => prev || optimistic);
    if (!companyId) return;
    saveKickoffCallBooking(companyId, { ...details, bookedByEmail: currentUser?.email || '' })
      .then((booking) => booking && setCallBooking(booking))
      .catch((err) => {
        console.error('Error saving kickoff call booking:', err);
        savingBookingRef.current = false;
      });
  }, [companyId, currentUser?.email]);

  // While the stepper is active, flag the body so the sidebar can paint the
  // current page's item green (see Sidebar.css).
  const tourActive = tourIndex != null;
  useEffect(() => {
    if (!tourActive) return undefined;
    document.body.classList.add('bba-tour-active');
    return () => document.body.classList.remove('bba-tour-active');
  }, [tourActive]);

  // Exit the stepper only when a navigation it didn't initiate lands somewhere
  // else (sidebar click). Our own Back/Next navigations are tracked in
  // tourNavRef so the in-flight frame doesn't bounce back to the pending page.
  // Child paths count as still on the stop — tab hubs like /portal/operations
  // immediately redirect to /portal/operations/all, and that must not exit the
  // tour. The dashboard stop ('/portal') stays exact-match, since every portal
  // path is technically under it. Keyed on location.key (not pathname) so
  // re-clicking the CURRENT page in the sidebar — a same-path navigation —
  // also exits a locked stop back to the gated page like any other click.
  useEffect(() => {
    if (tourIndex == null) return;
    const stop = TOUR_STOPS[tourIndex];
    const expected = normalizePath(stop?.path);
    const current = normalizePath(location.pathname);
    const withinStop =
      current === expected ||
      (expected !== '/portal' && current.startsWith(`${expected}/`));
    if (tourNavRef.current === expected) {
      if (withinStop) tourNavRef.current = null; // our navigation arrived
      return;
    }
    if (!withinStop) {
      setTourIndex(null);
      return;
    }
    // Foreign navigation landing exactly on a locked stop's root = the client
    // clicked this page's own sidebar item. Interactive stops ignore this
    // (their in-page navigation is legitimate).
    if (!stop.interactive && current === expected) setTourIndex(null);
  }, [location.key, tourIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Interactive pages can hand us a step index to resume the stepper at (e.g.
  // the Priorities page's "Continue Tour" button) via router state. This runs
  // AFTER the exit-on-foreign-navigation effect so the resume wins the commit.
  useEffect(() => {
    const resumeAt = location.state?.resumeTourAt;
    if (resumeAt == null || !isPendingStart) return;
    navigate(location.pathname, { replace: true, state: null });
    openTourAt(resumeAt);
  }, [location.state, location.pathname, isPendingStart, navigate, openTourAt]);

  const countdown = useCountdown(isPendingStart ? programStartDate : null);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <BBALoader size={96} />
      </div>
    );
  }

  // Not pending — or they watched the countdown hit zero — show the real portal.
  if (!isPendingStart || countdown?.done) {
    return <>{children}</>;
  }

  const tourStop = tourIndex != null ? TOUR_STOPS[tourIndex] : null;

  // Stepper mode: render the real page with the floating step bar. Interactive
  // steps (kickoff, profile) stay fully usable; tour pages are locked previews.
  if (tourStop) {
    const isFirst = tourIndex === 0;
    const isLast = tourIndex === TOUR_STOPS.length - 1;
    // Back/Next still run straight through the whole array (finishing the
    // kickoff steps rolls into the tour), but the counter and dots are scoped to
    // the Getting Started step they're inside, so "2 of 12" matches the cards.
    const phaseSteps = tourStop.phase === 'kickoff' ? KICKOFF_STEPS : TOUR_PAGE_STEPS;
    const phaseLabel = tourStop.phase === 'kickoff' ? 'Portal Kickoff' : 'Portal Tour';
    const phasePosition = phaseSteps.findIndex((stop) => stop.index === tourIndex) + 1;
    return (
      <>
        {/* Bottom padding keeps the floating step bar from covering the end of
            the page (e.g. the HR priority cards on the Priorities step). */}
        {tourStop.interactive ? (
          <div className="pb-48">{children}</div>
        ) : (
          <div className="pb-48 pointer-events-none select-none" aria-hidden="true">
            {children}
          </div>
        )}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-xl">
          <div
            className={`rounded-xl border shadow-2xl p-4 ${
              darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-3xl leading-none">{stopEmoji(tourStop)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{tourStop.title}</h3>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {phaseLabel} · {phasePosition} of {phaseSteps.length}
                  </span>
                </div>
                <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {tourStop.description}
                </p>
                <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {tourStop.hint ||
                    (tourStop.interactive
                      ? 'Complete this step, then hit Next.'
                      : 'Preview only — this page unlocks on your start date.')}
                </p>
              </div>
              <button
                type="button"
                onClick={closeTour}
                className={`p-1.5 rounded-lg ${
                  darkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'
                }`}
                title="Exit"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex justify-between items-center mt-3">
              <button
                type="button"
                onClick={() => openTourAt(tourIndex - 1)}
                disabled={isFirst}
                className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border ${
                  darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                } ${isFirst ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <div className="flex gap-1.5">
                {phaseSteps.map((stop) => (
                  <button
                    key={stop.id}
                    type="button"
                    onClick={() => openTourAt(stop.index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      stop.index === tourIndex
                        ? 'bg-red-600'
                        : darkMode
                        ? 'bg-gray-700 hover:bg-gray-600'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    title={stop.title}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!isLast) {
                    openTourAt(tourIndex + 1);
                    return;
                  }
                  // Finishing the tour returns to the dashboard — the last stop
                  // (Collection) stays usable on its own, so without this
                  // they'd be stranded there.
                  closeTour();
                  navigate('/portal');
                }}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700"
              >
                {isLast ? 'Finish' : 'Next'} {!isLast && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Outside the stepper, kickoff, profile, and collection remain fully usable
  // on their own (set everything up, sign out, reach their documents).
  if (
    location.pathname.startsWith('/portal/profile') ||
    location.pathname.startsWith('/portal/kickoff') ||
    location.pathname.startsWith('/portal/collection')
  ) {
    return <>{children}</>;
  }

  const startLabel = programStartDate
    ? programStartDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const cardClass = `rounded-lg border p-4 ${
    darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
  }`;
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textMain = darkMode ? 'text-gray-100' : 'text-gray-900';

  const tiles = countdown
    ? [
        { label: 'Days', value: countdown.days },
        { label: 'Hours', value: countdown.hours },
        { label: 'Minutes', value: countdown.minutes },
        { label: 'Seconds', value: countdown.seconds },
      ]
    : [];

  // Getting Started is three steps: book the kickoff call, complete the kickoff
  // pages (priorities + profile), then walk the tour. Steps 2 and 3 both drive
  // the same stepper — they just enter it at their own phase's first stop.
  const callScheduled = Boolean(callBooking);
  const kickoffDone = KICKOFF_STEPS.every((stop) => visitedTourStops.has(stop.index));
  const tourDone = TOUR_PAGE_STEPS.every((stop) => visitedTourStops.has(stop.index));
  const resumeIndex = (steps) =>
    (steps.find((stop) => !visitedTourStops.has(stop.index)) || steps[0]).index;
  const bookedLabel = callBooking?.scheduledAt
    ? new Date(callBooking.scheduledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  const gettingStartedSteps = [
    {
      id: 'schedule-call',
      emoji: '📅',
      title: 'Schedule Your Kickoff Call',
      description: callScheduled
        ? `Booked${bookedLabel ? ` on ${bookedLabel}` : ''} — check your email for the calendar invite.`
        : 'Pick a time with your success manager. This is where we set the plan for your first 90 days.',
      done: callScheduled,
      actionLabel: schedulerOpen ? 'Hide scheduler' : callScheduled ? 'Reschedule' : 'Pick a time',
      onAction: () => setSchedulerOpen((open) => !open),
    },
    {
      id: 'portal-kickoff',
      emoji: '🚀',
      title: 'Portal Kickoff',
      description: 'Rank your priorities across every department, then set up your profile and company info.',
      done: kickoffDone,
      actionLabel: kickoffDone
        ? 'Review kickoff'
        : KICKOFF_STEPS.some((stop) => visitedTourStops.has(stop.index))
        ? 'Continue kickoff'
        : 'Start kickoff',
      onAction: () => openTourAt(resumeIndex(KICKOFF_STEPS)),
    },
    {
      id: 'portal-tour',
      emoji: '🗺️',
      title: 'Portal Tour',
      description: 'Take the page-by-page tour so nothing in the portal is a surprise on day one.',
      done: tourDone,
      actionLabel: tourDone ? 'Tour again' : TOUR_PAGE_STEPS.some((s) => visitedTourStops.has(s.index)) ? 'Continue tour' : 'Start tour',
      onAction: () => openTourAt(tourDone ? TOUR_PAGE_STEPS[0].index : resumeIndex(TOUR_PAGE_STEPS)),
    },
  ];

  return (
    <div className={`p-6 ${textMain}`}>
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto space-y-8">
        <header className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <span className="text-4xl leading-none" aria-hidden="true">🚀</span>
          </div>
          <h1 className="text-3xl font-bold text-center">Your Program Starts Soon!</h1>
          <p className={`mt-2 text-center ${textMuted}`}>
            Welcome aboard! Your portal unlocks on{' '}
            {startLabel ? <span className={`font-semibold ${textMain}`}>{startLabel}</span> : 'your program start date'}.
          </p>
        </header>

        {tiles.length > 0 && (
          <div className="flex justify-center gap-3">
            {tiles.map((tile) => (
              <div
                key={tile.label}
                className="w-20 sm:w-24 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow p-3 text-center"
              >
                <div className="text-3xl font-bold tabular-nums">{pad(tile.value)}</div>
                <div className={`text-[11px] uppercase tracking-wide ${textMuted}`}>{tile.label}</div>
              </div>
            ))}
          </div>
        )}

        <section>
          <header className="text-center mb-4">
            <h2 className={`text-2xl font-semibold text-center ${textMain}`}>Getting Started</h2>
            <p className={`mt-1 text-sm text-center ${textMuted}`}>
              Three things to knock out before your start date — book your kickoff call, complete your
              portal kickoff, then take a page-by-page tour.
            </p>
          </header>
          <div className="grid gap-4 md:grid-cols-3">
            {gettingStartedSteps.map((step, idx) => (
              <div
                key={step.id}
                className={`${cardClass} relative flex flex-col items-center text-center gap-2 pt-8 pb-5 ${
                  step.done ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-600' : ''
                }`}
              >
                <span
                  className={`absolute top-2 left-2 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                    step.done
                      ? 'bg-green-500 text-white'
                      : darkMode
                      ? 'bg-gray-700 text-gray-300'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step.done ? <Check className="w-3 h-3" /> : idx + 1}
                </span>
                <span className="text-3xl leading-none">{step.emoji}</span>
                <h3 className={`text-base font-semibold text-center ${textMain}`}>{step.title}</h3>
                <p className={`text-xs text-center ${textMuted}`}>{step.description}</p>
                <button
                  type="button"
                  onClick={step.onAction}
                  className={`mt-auto pt-3 inline-flex items-center gap-1 text-sm font-semibold ${
                    step.done ? 'text-green-700 dark:text-green-400' : 'text-red-600 hover:text-red-700'
                  }`}
                >
                  {step.actionLabel} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {schedulerOpen && (
            <div className="mt-4 space-y-2">
              <KickoffCallScheduler darkMode={darkMode} onScheduled={handleCallScheduled} />
              <div className="flex justify-center gap-4">
                {!callScheduled && (
                  <button
                    type="button"
                    onClick={() => handleCallScheduled({ source: 'manual' })}
                    className={`text-xs underline ${textMuted} hover:text-gray-700 dark:hover:text-gray-200`}
                  >
                    I've already booked my kickoff call
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSchedulerOpen(false)}
                  className={`text-xs underline ${textMuted}`}
                >
                  Hide scheduler
                </button>
              </div>
            </div>
          )}
        </section>

        <section>
          <header className="text-center mb-4">
            <h2 className={`text-2xl font-semibold text-center ${textMain}`}>Portal Tour</h2>
            <p className={`mt-1 text-sm text-center ${textMuted}`}>
              A page-by-page look at your new portal. Jump to any stop — they unlock for real on your start date.
            </p>
          </header>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TOUR_PAGE_STEPS.map((stop, idx) => {
              const visited = visitedTourStops.has(stop.index);
              return (
                <button
                  key={stop.id}
                  type="button"
                  onClick={() => openTourAt(stop.index)}
                  className={`${cardClass} relative flex flex-col items-center justify-center py-5 gap-2 transition-shadow hover:shadow-md ${
                    visited ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-600' : ''
                  }`}
                >
                  <span
                    className={`absolute top-2 left-2 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                      visited
                        ? 'bg-green-500 text-white'
                        : darkMode
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <span className="text-3xl leading-none">{stopEmoji(stop)}</span>
                  <span className={`text-sm font-medium text-center ${textMain}`}>{stop.title}</span>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PortalPendingStartGate;
